// MeasurePanel — плавающая панель результатов инструмента «Измерение»
// (аналог диалога Measure во Fusion 360). Показывает:
//   • при 1 выборе  — свойства сущности (длина/площадь/периметр/радиус/позиция);
//   • при 2 выборах — минимальное расстояние, угол (если применимо),
//     ΔX/ΔY/ΔZ (для вершина↔вершина) + свойства каждой сущности.
// Значения кликабельны — копируются в буфер обмена.

import React from 'react';
import useCadStore from '../store/useCadStore';
import { clearMeasurePicks } from '../view3d/measureController';

const KIND_RU = { face: 'Грань', edge: 'Ребро', vertex: 'Точка' };
const GEOM_RU = {
    PLANE: 'плоская', CYLINDRE: 'цилиндр', CONE: 'конус', SPHERE: 'сфера',
    TORUS: 'тор', LINE: 'прямая', CIRCLE: 'окружность', ELLIPSE: 'эллипс',
};

function fmt(v, digits = 2, unit = 'мм') {
    if (typeof v !== 'number' || !isFinite(v)) return '—';
    return `${v.toFixed(digits)} ${unit}`.trim();
}

function copy(text) {
    try { navigator.clipboard?.writeText(String(text)); } catch (_) {}
}

// Свойства одной сущности → массив строк [label, value].
function entityRows(ent) {
    if (!ent) return [];
    const rows = [];
    if (ent.kind === 'vertex' && ent.position) {
        rows.push(['Позиция X', fmt(ent.position[0])]);
        rows.push(['Позиция Y', fmt(ent.position[1])]);
        rows.push(['Позиция Z', fmt(ent.position[2])]);
    }
    if (ent.kind === 'edge') {
        if (ent.length != null) rows.push(['Длина', fmt(ent.length)]);
        if (ent.geomType === 'CIRCLE') {
            if (ent.radius != null) rows.push(['Радиус', fmt(ent.radius)]);
            if (ent.diameter != null) rows.push(['Диаметр', fmt(ent.diameter)]);
            if (ent.center) rows.push(['Центр', `${ent.center.map((c) => c.toFixed(1)).join(', ')}`]);
        }
    }
    if (ent.kind === 'face') {
        if (ent.area != null) rows.push(['Площадь', fmt(ent.area / 100, 2, 'см²')]);
        if (ent.loopLength != null) rows.push(['Периметр', fmt(ent.loopLength)]);
        if (ent.geomType === 'CYLINDRE') {
            if (ent.radius != null) rows.push(['Радиус', fmt(ent.radius)]);
            if (ent.diameter != null) rows.push(['Диаметр', fmt(ent.diameter)]);
        }
    }
    return rows;
}

export default function MeasurePanel() {
    const pf = useCadStore((s) => s.pendingFeature);
    if (!pf || pf.kind !== 'measure') return null;

    const picks = Array.isArray(pf.picks) ? pf.picks : [];
    const res = pf.result;

    const label = { color: '#9aa4b2' };
    const val = { color: '#e8ecf1', fontWeight: 700, cursor: 'pointer' };
    const row = (k, v, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
            padding: '4px 0', borderBottom: '1px solid #262b33' }}>
            <span style={label}>{k}</span>
            <span style={val} title="Копировать" onClick={() => copy(v)}>{v}</span>
        </div>
    );

    const section = (title, children) => (
        <div style={{ marginTop: 10 }}>
            <div style={{ color: '#e8b73a', fontSize: 11, textTransform: 'uppercase',
                letterSpacing: 0.5, marginBottom: 4 }}>{title}</div>
            {children}
        </div>
    );

    return (
        <div style={{
            position: 'fixed', top: 96, right: 16, zIndex: 2500,
            width: 260, maxWidth: '86vw',
            background: '#1a1d23', border: '1px solid #363b44', borderRadius: 8,
            color: '#e8ecf1', font: '13px system-ui',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #33383f' }}>
                <b>Измерение</b>
                <button onClick={clearMeasurePicks} title="Сбросить выбор" style={{
                    background: 'none', border: '1px solid #363b44', color: '#9aa4b2',
                    borderRadius: 4, cursor: 'pointer', fontSize: 12, padding: '2px 8px',
                }}>Сброс</button>
            </div>

            <div style={{ padding: '10px 14px' }}>
                {picks.length === 0 && (
                    <div style={{ color: '#9aa4b2', lineHeight: 1.5 }}>
                        Кликните по грани, ребру или точке.<br/>
                        Второй клик — расстояние/угол.
                    </div>
                )}

                {res && res.error && (
                    <div style={{ color: '#cc6666' }}>{res.error}</div>
                )}

                {/* Результат 2 выборов */}
                {res && !res.error && res.count === 2 && (
                    <>
                        {res.distance != null && section('Расстояние', (
                            <>
                                {row('Минимальное', fmt(res.distance))}
                                {/* Для параллельных плоскостей минимальное расстояние
                                    часто меряется по краям (а если грани соприкасаются
                                    углом — вообще ноль). Человеку нужна величина вдоль
                                    нормали — показываем её отдельной строкой. */}
                                {res.planeDistance != null
                                    && row('Между плоскостями', fmt(res.planeDistance))}
                                {res.delta && res.bothVertices && (
                                    <>
                                        {row('ΔX', fmt(res.delta[0]))}
                                        {row('ΔY', fmt(res.delta[1]))}
                                        {row('ΔZ', fmt(res.delta[2]))}
                                    </>
                                )}
                            </>
                        ))}
                        {res.angle != null && section('Угол', (
                            row('Между гранями/рёбрами', fmt(res.angle, 2, '°'))
                        ))}
                    </>
                )}

                {/* Свойства выбранных сущностей */}
                {res && !res.error && res.entities && res.entities.map((ent, idx) => {
                    const rows = entityRows(ent);
                    if (rows.length === 0 && !ent) return null;
                    const title = `${KIND_RU[ent.kind] || ent.kind}`
                        + (ent.geomType && GEOM_RU[ent.geomType] ? ` (${GEOM_RU[ent.geomType]})` : '');
                    return section(res.count === 2 ? `${idx + 1}. ${title}` : title,
                        rows.length ? rows.map((r, i) => row(r[0], r[1], i))
                                    : <div style={{ color: '#9aa4b2' }}>—</div>);
                })}

                {/* Ожидание расчёта */}
                {picks.length > 0 && !res && (
                    <div style={{ color: '#9aa4b2', marginTop: 6 }}>Считаю…</div>
                )}
            </div>
        </div>
    );
}
